import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, Platform, Linking, AppState } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';
import { useModalBottomPadding } from '../utils/useModalBottomPadding';

/** QRScannerModal - fullscreen camera modal for scanning QR codes. */
interface QRScannerModalProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onScanned: (data: string) => void;
}

// ─── Web scanner (html5-qrcode) ───────────────────────────────────────────────

function WebQRScanner({ visible, onClose, onScanned }: QRScannerModalProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const scannerRef = useRef<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!visible) return;
    setError(null);
    setStarting(true);

    let cancelled = false;
    import('html5-qrcode').then(({ Html5Qrcode }) => {
      if (cancelled || !mountedRef.current) return;
      const scanner = new Html5Qrcode('rinsepay-qr-reader');
      scannerRef.current = scanner;

      scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
        (decodedText: string) => {
          scanner.stop().catch(() => {});
          scannerRef.current = null;
          onScanned(decodedText);
        },
        () => {},
      ).then(() => {
        if (mountedRef.current) setStarting(false);
      }).catch(() => {
        if (mountedRef.current) {
          setStarting(false);
          setError('Could not access camera. Please ensure camera permissions are granted.');
        }
      });
    }).catch(() => {
      if (mountedRef.current) {
        setStarting(false);
        setError('Failed to load scanner.');
      }
    });

    return () => {
      cancelled = true;
      scannerRef.current?.stop().catch(() => {});
      scannerRef.current = null;
    };
  }, [visible]);

  const handleClose = () => {
    scannerRef.current?.stop().catch(() => {});
    scannerRef.current = null;
    onClose();
  };

  if (!visible) return null;

  return (
    <Modal visible={visible} animationType="slide">
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={handleClose} style={styles.closeBtn} hitSlop={16}>
            <Ionicons name="close" size={28} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Scan QR Code</Text>
          <View style={{ width: 44 }} />
        </View>

        {error ? (
          <View style={styles.centered}>
            <Ionicons name="camera-outline" size={64} color="#aaa" />
            <Text style={styles.permText}>{error}</Text>
            <TouchableOpacity style={styles.cancelBtn} onPress={handleClose}>
              <Text style={styles.cancelBtnText}>Close</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            {starting && <Text style={styles.permText}>Starting camera...</Text>}
            {/* html5-qrcode mounts into this div */}
            <div id="rinsepay-qr-reader" style={{ width: '100%', maxWidth: 400 }} />
            <Text style={[styles.hintText, { marginTop: 16 }]}>Point at a RinsePay QR code</Text>
          </View>
        )}
      </View>
    </Modal>
  );
}

// ─── Native scanner (react-native-vision-camera) ──────────────────────────────
//
// We used to depend on expo-camera's <CameraView> for QR scanning.  The
// migration off Expo swapped it for react-native-vision-camera which has
// a different API: useCameraDevice + useCameraPermission hooks and a
// Camera component that consumes a codeScanner frame processor.  The
// permission tri-state (null/true/false) from expo-camera is collapsed
// into hasPermission: boolean and a separate "has ever been asked" flag
// that we approximate with a local ref.

// Top-level import resolves to the vision-camera web stub on web (via
// Vite alias) and to the real native module on Android/iOS.  The
// underlying implementations are no-ops on web, so the NativeQRScanner
// component is simply never rendered on that platform — the web path
// below uses html5-qrcode instead.
import {
  Camera,
  useCameraDevice,
  useCameraPermission,
  useCodeScanner,
} from 'react-native-vision-camera';

function NativeQRScanner({ visible, onClose, onScanned }: QRScannerModalProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const modalBottomPadding = useModalBottomPadding();
  const { hasPermission, requestPermission } = useCameraPermission();
  const device = useCameraDevice('back');
  const [scanned, setScanned] = useState(false);
  const [cameraLayout, setCameraLayout] = useState<{ width: number; height: number } | null>(null);
  // Track whether we've asked at least once this session so we can show
  // the "open settings" fallback after an initial denial — vision-camera
  // doesn't expose a canAskAgain flag directly.
  const hasRequestedRef = useRef(false);
  const canAskAgain = !hasRequestedRef.current || hasPermission;

  const codeScanner = useCodeScanner
    ? useCodeScanner({
        codeTypes: ['qr'],
        onCodeScanned: (codes: Array<{ value?: string; type?: string }>) => {
          if (scanned) return;
          const first = codes?.[0];
          if (!first?.value) return;
          setScanned(true);
          onScanned(first.value);
        },
      })
    : undefined;

  useEffect(() => {
    if (!visible) return;
    if (!hasPermission) {
      hasRequestedRef.current = true;
      requestPermission();
    }
  }, [visible, hasPermission, requestPermission]);

  useEffect(() => {
    if (!visible) return;
    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active' && hasPermission === false) {
        // Re-poll in case the user just granted the permission in Settings
        requestPermission();
      }
    });
    return () => subscription.remove();
  }, [visible, hasPermission, requestPermission]);

  useEffect(() => {
    if (visible) { setScanned(false); setCameraLayout(null); }
  }, [visible]);

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="fullScreen">
      <View style={[styles.container, { paddingBottom: modalBottomPadding }]}>
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeBtn} hitSlop={16}>
            <Ionicons name="close" size={28} color={colors.white} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Scan QR Code</Text>
          <View style={{ width: 44 }} />
        </View>

        {hasPermission === false ? (
          <View style={styles.centered}>
            <Ionicons name="camera-outline" size={64} color={colors.textLight} />
            <Text style={styles.permText}>Camera access is required to scan QR codes.</Text>
            {!canAskAgain ? (
              <>
                <Text style={styles.permSubtext}>
                  Camera permission was denied. Please enable it in your device settings.
                </Text>
                <TouchableOpacity style={[styles.settingsBtn, { backgroundColor: colors.primary }]} onPress={() => Linking.openSettings()}>
                  <Ionicons name="settings-outline" size={20} color="#fff" />
                  <Text style={styles.settingsBtnText}>Open Settings</Text>
                </TouchableOpacity>
              </>
            ) : (
              <TouchableOpacity style={[styles.settingsBtn, { backgroundColor: colors.primary }]} onPress={requestPermission}>
                <Ionicons name="camera" size={20} color="#fff" />
                <Text style={styles.settingsBtnText}>Grant Permission</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose}>
              <Text style={styles.cancelBtnText}>Close</Text>
            </TouchableOpacity>
          </View>
        ) : hasPermission === null ? (
          <View style={styles.centered}>
            <Text style={styles.permText}>Loading camera...</Text>
          </View>
        ) : (
          <View
            style={styles.cameraWrap}
            onLayout={(e) => {
              const { width, height } = e.nativeEvent.layout;
              if (width > 0 && height > 0) setCameraLayout({ width, height });
            }}
          >
            {cameraLayout && device ? (
              <Camera
                style={{ width: cameraLayout.width, height: cameraLayout.height }}
                device={device}
                isActive={visible && !scanned}
                codeScanner={codeScanner}
              />
            ) : (
              <View style={styles.centered}>
                <Text style={styles.permText}>Starting camera...</Text>
              </View>
            )}
            <View style={styles.overlay}>
              <View style={styles.overlayTop} />
              <View style={styles.overlayMiddle}>
                <View style={styles.overlaySide} />
                <View style={styles.scanBox}>
                  <View style={[styles.corner, styles.cornerTL]} />
                  <View style={[styles.corner, styles.cornerTR]} />
                  <View style={[styles.corner, styles.cornerBL]} />
                  <View style={[styles.corner, styles.cornerBR]} />
                </View>
                <View style={styles.overlaySide} />
              </View>
              <View style={styles.overlayBottom}>
                <Text style={styles.hintText}>Point at a RinsePay QR code</Text>
                {scanned && (
                  <TouchableOpacity style={styles.rescanBtn} onPress={() => setScanned(false)}>
                    <Text style={styles.rescanText}>Tap to Scan Again</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </View>
        )}
      </View>
    </Modal>
  );
}

// ─── Export ────────────────────────────────────────────────────────────────────

export default function QRScannerModal(props: QRScannerModalProps) {
  if (Platform.OS === 'web') return <WebQRScanner {...props} />;
  return <NativeQRScanner {...props} />;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const SCAN_BOX = 250;
const CORNER_SIZE = 30;
const CORNER_WIDTH = 4;

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: '#000' },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingTop: 54, paddingBottom: spacing.md, paddingHorizontal: spacing.md,
    backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10,
  },
  closeBtn: { width: 44, height: 44, justifyContent: 'center', alignItems: 'center' },
  headerTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: '#fff' },

  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },
  permText: { fontSize: fontSize.md, color: '#aaa', textAlign: 'center', marginTop: spacing.md, maxWidth: 280 },
  permSubtext: { fontSize: fontSize.sm, color: '#888', textAlign: 'center', marginTop: spacing.sm, maxWidth: 280 },
  settingsBtn: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    marginTop: spacing.lg, paddingVertical: spacing.md, paddingHorizontal: spacing.xl,
    borderRadius: borderRadius.md,
  },
  settingsBtnText: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: '#fff' },
  cancelBtn: { marginTop: spacing.lg, paddingVertical: spacing.sm, paddingHorizontal: spacing.xl },
  cancelBtnText: { fontSize: fontSize.md, color: '#aaa' },

  cameraWrap: { flex: 1 },
  overlay: { ...StyleSheet.absoluteFillObject },
  overlayTop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)' },
  overlayMiddle: { flexDirection: 'row', height: SCAN_BOX },
  overlaySide: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)' },
  scanBox: { width: SCAN_BOX, height: SCAN_BOX },
  overlayBottom: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', paddingTop: spacing.lg },
  hintText: { fontSize: fontSize.md, color: '#fff', fontWeight: fontWeight.medium },
  rescanBtn: {
    marginTop: spacing.md, paddingVertical: spacing.sm, paddingHorizontal: spacing.lg,
    backgroundColor: c.primary, borderRadius: borderRadius.md,
  },
  rescanText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: '#fff' },

  corner: { position: 'absolute', width: CORNER_SIZE, height: CORNER_SIZE },
  cornerTL: { top: 0, left: 0, borderTopWidth: CORNER_WIDTH, borderLeftWidth: CORNER_WIDTH, borderColor: '#fff' },
  cornerTR: { top: 0, right: 0, borderTopWidth: CORNER_WIDTH, borderRightWidth: CORNER_WIDTH, borderColor: '#fff' },
  cornerBL: { bottom: 0, left: 0, borderBottomWidth: CORNER_WIDTH, borderLeftWidth: CORNER_WIDTH, borderColor: '#fff' },
  cornerBR: { bottom: 0, right: 0, borderBottomWidth: CORNER_WIDTH, borderRightWidth: CORNER_WIDTH, borderColor: '#fff' },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
